import { useState } from 'react';
import { ArrowRight } from 'lucide-react';

interface Props {
  onBoostPass: (tier: 'premium_economy' | 'first_class', duration: string, price: number) => void;
}

type Tier = 'premium_economy' | 'first_class';

const PRICES: Record<Tier, Record<string, number>> = {
  premium_economy: { '3-Day': 2.99, '7-Day': 5.99, '14-Day': 9.99 },
  first_class: { '3-Day': 4.99, '7-Day': 10.99, '14-Day': 16.99 },
};

const INCLUDES: Record<Tier, string> = {
  premium_economy: 'Unlimited AI packing lists, visa checks, health preparation, local tips, currency tools and group expenses.',
  first_class: 'Everything in Premium Economy plus multi-city packing, offline mode, reusable templates, travel analytics and group progress sharing.',
};

const DURATIONS = ['3-Day', '7-Day', '14-Day'];

export const BoostPassesSection = ({ onBoostPass }: Props) => {
  const [tier, setTier] = useState<Tier>('premium_economy');
  const [duration, setDuration] = useState('3-Day');
  const price = PRICES[tier][duration];
  const days = duration.replace('-Day', '');

  return (
    <section id="boost-passes" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 scroll-mt-24" aria-labelledby="boost-heading">
      <div className="grid md:grid-cols-2 gap-8 md:gap-14 items-stretch">
        <div className="bg-primary text-primary-foreground p-8 sm:p-10 flex flex-col justify-center min-h-[280px] md:min-h-[520px]">
          <p className="text-xs font-semibold uppercase tracking-wide opacity-80">Every tool. One payment.</p>
          <p className="mt-5 text-6xl sm:text-7xl font-bold leading-none" aria-label="3, 7 or 14">
            3<span className="opacity-50">·7·14</span>
          </p>
          <p className="mt-3 text-sm opacity-80">days of access</p>
          <p className="mt-8 text-xl sm:text-2xl font-bold leading-snug">
            Pack, check and go.<br />No subscription, ever.
          </p>
        </div>

        <div className="flex flex-col justify-center">
          <h2 id="boost-heading" className="text-sm font-bold uppercase tracking-wide text-primary mb-5">Boost Pass</h2>

          <div role="tablist" aria-label="Boost Pass tier" className="grid grid-cols-2 gap-1 rounded-md bg-primary/10 p-1.5 mb-5">
            {(['premium_economy', 'first_class'] as Tier[]).map((t) => (
              <button
                key={t}
                role="tab"
                aria-selected={tier === t}
                onClick={() => setTier(t)}
                className={`min-h-[48px] rounded text-sm sm:text-base font-medium transition-colors ${
                  tier === t ? 'bg-background text-primary shadow-sm' : 'text-foreground/70 hover:text-foreground'
                }`}
              >
                {t === 'premium_economy' ? 'Premium Economy' : 'First Class'}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6" role="radiogroup" aria-label="Pass length">
            {DURATIONS.map((d) => (
              <button
                key={d}
                role="radio"
                aria-checked={duration === d}
                onClick={() => setDuration(d)}
                className={`min-h-[56px] rounded border text-sm sm:text-base font-medium transition-colors ${
                  duration === d ? 'border-primary bg-primary/10 text-primary' : 'border-border text-foreground hover:border-primary/50'
                }`}
              >
                {d.replace('-Day', ' Days')}
              </button>
            ))}
          </div>

          <p className="flex items-baseline gap-4 mb-4">
            <span className="text-5xl font-bold text-foreground">€{price.toFixed(2)}</span>
            <span className="text-base text-muted-foreground">one-off payment</span>
          </p>
          <p className="text-base leading-relaxed text-muted-foreground mb-6">{INCLUDES[tier]}</p>
          <p className="text-base text-muted-foreground mb-6">{days} days of access. No subscription, no auto-renewal.</p>

          <button
            onClick={() => onBoostPass(tier, duration, price)}
            className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full rounded bg-primary text-primary-foreground text-lg font-bold hover:bg-primary/90 transition-colors"
          >
            Buy Boost Pass <ArrowRight className="h-5 w-5" />
          </button>
          <p className="mt-4 text-sm text-muted-foreground">Access starts when you pay and ends automatically.</p>
        </div>
      </div>
    </section>
  );
};
